import { useLayoutEffect, useMemo, useRef, useState } from 'react';

// Positioning for the floating row-action menus (the "⋯" in every table's
// Action column). The menu always opens directly below its button,
// right-aligned to it. It is measured before paint so it never appears
// somewhere else first; when there isn't room below, the page scrolls just
// enough to make room (the menu stays attached to the button while it does),
// and it follows the button on any later scroll or resize.

const GAP = 6;     // space between the button and the menu
const MARGIN = 8;  // minimum distance from the viewport edges

// Snapshot of the trigger button's rect that also carries the element itself,
// so the menu can follow the button on scroll. Use in place of
// `button.getBoundingClientRect()` when opening a menu.
export function anchorRect(el) {
  if (!el?.getBoundingClientRect) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height, anchor: el };
}

// The app shell renders at `zoom: 0.75` (MainLayout / .app-shell-zoom-75).
// Menus are portalled to <body>, outside that shell, so they re-apply the
// same zoom to keep the table's size and font. This finds the zoom in effect
// at the button.
function effectiveZoom(el) {
  let zoom = 1;
  for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
    const value = parseFloat(window.getComputedStyle(node).zoom);
    if (value && value !== 1) zoom *= value;
  }
  return zoom;
}

// All maths here is in on-screen pixels: the button's rect, and the menu's
// rendered (zoomed) size.
function computePosition(rect, menuEl, align) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = menuEl.getBoundingClientRect().width;

  const top = rect.bottom + GAP;
  const maxHeight = Math.max(vh - top - MARGIN, 120);
  const preferred = align === 'left' ? rect.left : rect.right - width;
  const left = Math.max(MARGIN, Math.min(preferred, vw - width - MARGIN));

  return { top, left, maxHeight };
}

// Scrollable ancestors of `el`, nearest first, ending with the page itself.
function scrollParents(el) {
  const parents = [];
  for (let node = el?.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) parents.push(node);
  }
  parents.push(document.scrollingElement || document.documentElement);
  return parents;
}

// Scroll the button's containers up by however much the menu overflows the
// bottom of the viewport, so the whole menu fits below the button.
function makeRoomBelow(anchor, rect, menuEl, zoom) {
  let deficit = rect.bottom + GAP + menuEl.scrollHeight * zoom + MARGIN - window.innerHeight;
  if (deficit <= 0 || !anchor) return;
  // Never push the button itself off the top of the screen.
  deficit = Math.min(deficit, Math.max(rect.top - MARGIN, 0));
  for (const container of scrollParents(anchor)) {
    if (deficit <= 0) break;
    const room = container.scrollHeight - container.clientHeight - container.scrollTop;
    const step = Math.min(deficit, room);
    if (step > 0) {
      container.scrollBy({ top: step, behavior: 'smooth' });
      deficit -= step;
    }
  }
}

// `align`: 'right' lines the menu's right edge up with the button (row action
// menus in the last column); 'left' lines up the left edges (column filters).
// `anchorSelector` finds the button again if the table re-renders it.
export function useAnchoredMenu(rect, onClose, { align = 'right', anchorSelector = '.nlims-action-btn' } = {}) {
  const menuRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [pos, setPos] = useState(null);
  const zoom = useMemo(() => effectiveZoom(rect?.anchor), [rect]);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    if (!rect) return undefined;
    const close = () => onCloseRef.current?.();

    // Tables redraw their rows on parent re-renders (opening this menu is one),
    // which replaces the button element. When that happens, pick up the new
    // button sitting at the same spot; failing that, stay at the last known
    // position rather than closing.
    let anchor = rect.anchor;
    let lastRect = rect;
    const following = () => {
      if (!anchor) return false;
      if (anchor.isConnected) return true;
      const x = lastRect.left + lastRect.width / 2;
      const y = lastRect.top + lastRect.height / 2;
      const replacement = document.elementsFromPoint?.(x, y)
        .map((el) => el.closest?.(anchorSelector))
        .find(Boolean);
      if (replacement) anchor = replacement;
      return !!replacement;
    };

    const update = () => {
      const menu = menuRef.current;
      if (!menu) return;
      if (following()) {
        const r = anchor.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) { close(); return; }
        lastRect = r;
      }
      setPos(computePosition(lastRect, menu, align));
    };

    update();
    if (menuRef.current) makeRoomBelow(anchor, lastRect, menuRef.current, zoom);

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    // Re-check after the table's post-open redraw has run.
    schedule();
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return; // scrolling inside the menu itself
      // Without the button element there's nothing to follow — close instead of floating detached.
      if (following()) schedule(); else close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', schedule);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('keydown', onKey);
    };
  }, [rect, align, anchorSelector, zoom]);

  // First render is invisible at 0,0 so the menu can be measured; the layout
  // effect positions it before the browser paints, so it never visibly jumps.
  // Lengths on a zoomed element are scaled by its zoom, so on-screen pixel
  // values are divided by it.
  const style = pos
    ? { position: 'fixed', zoom, top: pos.top / zoom, left: pos.left / zoom, maxHeight: pos.maxHeight / zoom, overflowY: 'auto', zIndex: 9999 }
    : { position: 'fixed', zoom, top: 0, left: 0, visibility: 'hidden', zIndex: 9999 };

  return { menuRef, style };
}
